import {useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {yupResolver} from '@hookform/resolvers/yup';
import {Button} from '@mui/material';
import {isAxiosError} from 'axios';
import {useSnackbar} from 'notistack';

import {useCreateGeoFilter, useUploadAdminFile} from '@entities/common';
import {getErrorMessageFromApiError} from '@shared/api';
import {CardBox} from '@shared/components';
import {ROUTE__GEO_FILTER_OPTIONS} from '@shared/constants';
import {resolveEnglishText} from '@shared/helpers';
import {SupportedLanguage, TranslationDict} from '@shared/types';
import {FormRow, Header} from '@widgets/common';
import {FormFields} from './FormFields';
import {FormValues, v8nSchema} from './v8nSchema';

import {COLOR__WHITE} from '@/theme/colors';

export const CreateGeoFilterOptionPage = () => {
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();
  const {mutateAsync: createGeoFilter, isPending: isCreating} =
    useCreateGeoFilter();
  const {mutateAsync: uploadAdminFile, isPending: isUploading} =
    useUploadAdminFile();

  const [nameTranslations, setNameTranslations] = useState<TranslationDict>(
    {} as TranslationDict,
  );

  const formMethods = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(v8nSchema),
    defaultValues: {
      slug: '',
      name: '',
      logoFile: null,
    },
  });
  const {
    handleSubmit,
    setValue,
    formState: {dirtyFields},
  } = formMethods;

  const onSubmit = async (values: FormValues): Promise<void> => {
    try {
      const uploadedLogo = values.logoFile
        ? await uploadAdminFile({file: values.logoFile, type: 'category_logo'})
        : null;

      await createGeoFilter({
        slug: values.slug,
        nameTrans: {
          ...nameTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            nameTranslations,
            values.name,
            !!dirtyFields.name,
          ),
        },
        logoUrl: uploadedLogo?.key ?? undefined,
      });

      enqueueSnackbar('GEO filter created successfully', {variant: 'success'});
      navigate(ROUTE__GEO_FILTER_OPTIONS);
    } catch (error) {
      const errorMessage = isAxiosError(error)
        ? getErrorMessageFromApiError(error)
        : '';
      enqueueSnackbar(`Could not create GEO filter. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <Header title="Add new GEO category" mb={1} />

      <CardBox bgcolor={COLOR__WHITE} maxWidth={450}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormProvider {...formMethods}>
            <FormFields
              nameTranslations={nameTranslations}
              onNameTranslationsChange={translations => {
                setValue('name', translations[SupportedLanguage.English]);
                setNameTranslations(translations);
              }}
            />
          </FormProvider>

          <FormRow width={420} mt={2}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{flex: 1, border: '0.4px solid #CBCDD8 !important'}}
            >
              Back
            </Button>

            <Button
              type="submit"
              sx={{flex: 2}}
              disabled={isCreating || isUploading}
            >
              Add
            </Button>
          </FormRow>
        </form>
      </CardBox>
    </>
  );
};
